<template>
  <div class="product-detail-page">
    <!-- Not Found -->
    <template v-if="!product">
      <section class="not-found-section">
        <div class="content-container">
          <div class="not-found-content">
            <Icon icon="lucide:search-x" :size="48" color="#9CA3AF" />
            <h2 class="not-found-title">产品未找到</h2>
            <p class="not-found-desc">您访问的产品不存在或已被移除</p>
            <router-link to="/portal/products" class="btn-back">
              <Icon icon="lucide:arrow-left" :size="16" />
              返回产品列表
            </router-link>
          </div>
        </div>
      </section>
    </template>

    <!-- Product Found -->
    <template v-else>
      <!-- Hero Section -->
      <section
        class="hero-section"
        :style="{ background: `linear-gradient(135deg, ${product.color}, #001B5E)` }"
      >
        <div class="hero-overlay"></div>
        <div class="hero-content">
          <div class="hero-icon-box">
            <Icon :icon="product.icon" :size="40" color="#fff" />
          </div>
          <div class="hero-text">
            <div class="hero-breadcrumb">
              <router-link to="/portal/products" class="hero-breadcrumb-link">产品中心</router-link>
              <span class="hero-breadcrumb-sep">/</span>
              <span class="hero-breadcrumb-current">{{ product.category }}</span>
            </div>
            <h1 class="hero-title">{{ product.name }}</h1>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="main-section">
        <div class="content-container">
          <div class="main-grid">
            <!-- Left: Main Content -->
            <div class="main-left">
              <!-- Product Intro -->
              <div class="card intro-card">
                <h2 class="card-title">产品介绍</h2>
                <p class="intro-desc">
                  {{ product.desc }}。<b>{{ product.name }}</b
                  >是紫金山AI面向企业客户提供的一站式解决方案，支持插件化配置，3分钟快速上线。
                </p>
                <div class="features-grid">
                  <div v-for="(f, i) in product.features" :key="i" class="feature-item">
                    <div class="feature-icon" :style="{ background: product.bgColor }">
                      <Icon icon="lucide:check" :size="16" :color="product.color" />
                    </div>
                    <span class="feature-text">{{ f }}</span>
                  </div>
                </div>
              </div>

              <!-- CTA Card -->
              <div class="card cta-card">
                <h2 class="card-title">立即体验</h2>
                <p class="cta-desc">
                  {{
                    product.name
                  }}提供免费试用体验，您可以在试用期间充分体验所有核心功能。如有定制化需求，欢迎联系我们的销售团队获取专属方案。
                </p>
                <div class="cta-actions">
                  <button class="btn-primary-lg" @click="showTrialModal">
                    <Icon icon="lucide:rocket" :size="18" color="#fff" /> 免费试用
                  </button>
                  <button class="btn-outline-lg" @click="showTrialModal">
                    <Icon icon="lucide:phone" :size="18" /> 联系销售
                  </button>
                </div>
              </div>

              <!-- FAQ -->
              <div class="card faq-card">
                <h2 class="card-title">常见问题</h2>
                <div class="faq-list">
                  <div class="faq-item">
                    <p class="faq-text"
                      >Q: 购买后多久可以部署上线？<br />A:
                      支付成功后系统自动部署，3分钟即可完成。</p
                    >
                  </div>
                  <div class="faq-item">
                    <p class="faq-text"
                      >Q: 是否支持数据迁移？<br />A: 支持，提供一键数据迁移工具。</p
                    >
                  </div>
                  <div class="faq-item">
                    <p class="faq-text"
                      >Q: 如何升级版本？<br />A: 在个人中心可随时升级套餐，差价按天折算。</p
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Sidebar -->
            <div class="main-right">
              <div class="sidebar-card">
                <h3 class="sidebar-name">{{ product.name }}</h3>
                <div class="sidebar-price">
                  <span class="price-value" :style="{ color: product.color }">免费试用</span>
                </div>
                <p class="sidebar-desc">提供免费试用体验，满意后再决定是否购买。</p>
                <button class="sidebar-btn-primary" @click="showTrialModal">
                  <Icon icon="lucide:rocket" :size="16" color="#fff" /> 免费试用
                </button>
                <button class="sidebar-btn-outline" @click="showTrialModal">
                  <Icon icon="lucide:phone" :size="16" /> 联系销售
                </button>
                <div class="sidebar-features">
                  <p class="sidebar-features-label">包含功能</p>
                  <div v-for="(f, i) in product.features" :key="i" class="sidebar-feature-item">
                    <Icon icon="lucide:check-circle" :size="14" :color="product.color" />
                    <span>{{ f }}</span>
                  </div>
                </div>
              </div>

              <!-- Related Products -->
              <div v-if="relatedProducts.length" class="sidebar-card sidebar-related">
                <h3 class="sidebar-related-title">相关产品</h3>
                <div
                  v-for="rp in relatedProducts"
                  :key="rp.id"
                  class="related-item"
                  @click="goToDetail(rp.id)"
                >
                  <div class="related-icon" :style="{ background: rp.bgColor }">
                    <Icon :icon="rp.icon" :size="18" :color="rp.color" />
                  </div>
                  <div class="related-info">
                    <p class="related-name">{{ rp.name }}</p>
                    <p class="related-more">了解更多</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Back Link -->
          <div class="back-section">
            <router-link to="/portal/products" class="back-link">
              <Icon icon="lucide:arrow-left" :size="16" color="#0066FF" />
              返回产品列表
            </router-link>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import { products, type ProductItem } from '../data/portalData'
import { getPortalArticle } from '@/api/portal'
import {
  getPortalProductCatalog,
  getPortalProductNavigation,
  type PortalProductCatalogItem
} from '../data/portalCatalog'
import { usePortalState } from '../usePortalState'

defineOptions({ name: 'ProductDetailPage' })

const route = useRoute()
const router = useRouter()
const { showTrialModal } = usePortalState()

const product = ref<ProductItem | undefined>()

const relatedProducts = computed(() => {
  if (!product.value) return []
  return products
    .filter((p) => p.category === product.value!.category && p.id !== product.value!.id)
    .slice(0, 3)
})

function goToDetail(id: string) {
  router.push(`/portal/products/${id}`)
}

function fromCatalog(item: PortalProductCatalogItem): ProductItem {
  return {
    id: item.id,
    name: item.name,
    icon: item.icon,
    color: item.color,
    bgColor: item.bgColor,
    desc: item.desc,
    descShort: item.descShort,
    category: item.category,
    features: item.features,
    price: item.price,
    industries: item.industries
  }
}

/** 解析顺序：静态产品 → 产品中心 CMS → 数字文章 id → 产品导航 CMS（营销入口兜底） */
async function resolveProduct(id: string): Promise<ProductItem | undefined> {
  const key = String(id || '').trim()
  if (!key) return undefined

  const staticHit = products.find((p) => p.id === key)
  if (staticHit) return staticHit

  try {
    const catalog = await getPortalProductCatalog()
    const hit = catalog.find((p) => p.id === key)
    if (hit) return fromCatalog(hit)
  } catch {
    // CMS 产品目录不可用时继续兜底
  }

  if (/^\d+$/.test(key)) {
    try {
      const article = await getPortalArticle(key)
      if (article) {
        let extra: Record<string, unknown> = {}
        try {
          extra = JSON.parse(article.extra || '{}') as Record<string, unknown>
        } catch {
          extra = {}
        }
        return {
          id: String(extra.slug || article.id),
          name: article.title,
          icon: String(extra.icon || 'lucide:box'),
          color: String(extra.color || '#0066FF'),
          bgColor: String(extra.bgColor || '#EBF5FF'),
          desc: article.summary || '',
          descShort: String(extra.descShort || article.summary || ''),
          category: String(extra.category || '企业服务'),
          features: Array.isArray(extra.features) ? extra.features.map(String) : [],
          price: String(extra.price || ''),
          industries: Array.isArray(extra.industries) ? extra.industries.map(String) : []
        }
      }
    } catch {
      // ignore
    }
  }

  try {
    const nav = await getPortalProductNavigation()
    const hit = nav.find((p) => p.id === key)
    if (hit) return fromCatalog(hit)
  } catch {
    // ignore
  }

  return undefined
}

async function loadProduct() {
  const id = String(route.params.id || '')
  product.value = await resolveProduct(id)
}

onMounted(loadProduct)
watch(() => route.params.id, loadProduct)
</script>

<style lang="scss" scoped>
$brand-darker: #001b5e;

.product-detail-page {
  // navbar-spacer handled by Login.vue
}

.content-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}

// ===== Hero =====
.hero-section {
  height: 240px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;

  .hero-overlay {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1), transparent 70%);
  }
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
}

.hero-icon-box {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.hero-text {
  color: #fff;
}

.hero-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 8px;
}

.hero-breadcrumb-link {
  cursor: pointer;
  opacity: 0.7;
  color: #fff;
  text-decoration: none;

  &:hover {
    opacity: 1;
  }
}

.hero-breadcrumb-sep {
  opacity: 0.5;
}

.hero-breadcrumb-current {
  color: #fff;
}

.hero-title {
  font-size: 32px;
  font-weight: 800;
}

// ===== Main =====
.main-section {
  padding: 40px 24px 80px;
}

.main-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 32px;
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
}

.card-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 16px;
}

// Intro
.intro-desc {
  font-size: 16px;
  color: #4b5563;
  line-height: 1.8;
  margin-bottom: 24px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 10px;
}

.feature-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

// CTA Card
.cta-desc {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 24px;
}

.cta-actions {
  display: flex;
  gap: 12px;
}

.btn-primary-lg {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: #0066ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  justify-content: center;

  &:hover {
    background: #0044cc;
  }
}

.btn-outline-lg {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: #fff;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  justify-content: center;

  &:hover {
    border-color: #0066ff;
    color: #0066ff;
  }
}

// FAQ
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.faq-item {
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px;
}

.faq-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.8;
}

// ===== Sidebar =====
.sidebar-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 24px;
  position: sticky;
  top: 88px;
  margin-bottom: 24px;
}

.sidebar-name {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.sidebar-price {
  margin-bottom: 4px;
}

.price-value {
  font-size: 20px;
  font-weight: 700;
}

.sidebar-desc {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 20px;
  line-height: 1.6;
}

.sidebar-btn-primary {
  width: 100%;
  height: 48px;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #0066ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;

  &:hover {
    background: #0044cc;
  }
}

.sidebar-btn-outline {
  width: 100%;
  height: 48px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fff;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #0066ff;
    color: #0066ff;
  }
}

.sidebar-features {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
}

.sidebar-features-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}

.sidebar-feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #4b5563;
}

// Related Products
.sidebar-related-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 16px;
}

.related-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
}

.related-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.related-name {
  font-size: 14px;
  font-weight: 500;
}

.related-more {
  font-size: 12px;
  color: #9ca3af;
}

// ===== Back =====
.back-section {
  padding-top: 40px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #0066ff;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
}

// ===== Not Found =====
.not-found-section {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
}

.not-found-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .not-found-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
  }

  .not-found-desc {
    font-size: 14px;
    color: #6b7280;
  }

  .btn-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #0066ff;
    color: #fff;
    border-radius: 8px;
    font-size: 14px;
    text-decoration: none;

    &:hover {
      background: #0044cc;
    }
  }
}

// ===== Responsive =====
@media (max-width: 1024px) {
  .main-grid {
    grid-template-columns: 1fr;
  }

  .main-right {
    order: -1;
  }

  .sidebar-card {
    position: static;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 200px;
  }

  .hero-icon-box {
    width: 56px;
    height: 56px;
  }

  .hero-title {
    font-size: 24px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .cta-actions {
    flex-direction: column;
  }
}
</style>
